import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '@/constants/queryKeys';
import { formsTemplatesService } from '@/services/formsTemplates.service';
import type {
  GetFormTemplatesParams,
  SaveFormTemplateDraftPayload,
  UpdateFormTemplatePayload,
} from '@/types/formsTemplates';

export const useFormTemplateTags = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.FORM_TEMPLATE_TAGS],
    queryFn: () => formsTemplatesService.getFormTemplateTags(),
  });
};

export const useFormTemplates = (params: GetFormTemplatesParams = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.FORM_TEMPLATES, params],
    queryFn: () => formsTemplatesService.getFormTemplates(params),
  });
};

export const useFormTemplateById = (id: string | undefined) => {
  return useQuery({
    queryKey: [QUERY_KEYS.FORM_TEMPLATE_BY_ID, id],
    queryFn: () => formsTemplatesService.getFormTemplateById(id ?? ''),
    enabled: !!id,
  });
};

export const useSaveFormTemplateDraftMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SaveFormTemplateDraftPayload) => formsTemplatesService.saveFormTemplateDraft(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FORM_TEMPLATES] });
    },
  });
};

export const useUpdateFormTemplateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateFormTemplatePayload }) =>
      formsTemplatesService.updateFormTemplate(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FORM_TEMPLATES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FORM_TEMPLATE_BY_ID] });
    },
  });
};
